import { useEffect, useSyncExternalStore } from 'react'
import { readPref, savePref } from '@shared/services/prefsService'

/**
 * The website's two skins (Huanrui, 2026-10-02): the black it has always had,
 * and a light one in Miro's colours. Chosen with the sun-and-moon button in
 * the top bar and remembered in this browser.
 *
 * The sign-in page keeps the black whatever is chosen, and says so with
 * useDarkOnly(); while it is on screen the page is drawn dark. The choice
 * itself is not changed by it. (A project's canvas, the overview's colour
 * field and Amazon's opening light stay black inside a light page by CSS:
 * theme.css.)
 *
 * Kept with the account too (prefsService, 2026-10-02), so the skin follows a
 * person to another computer; the browser's copy draws the first frame.
 *
 * Applied as data-theme on <html>, which theme.css reads. Set as soon as this
 * module loads, so a light page never flashes black first.
 */

export type Theme = 'dark' | 'light'

const KEY = 'ww-theme'
let chosen: Theme = read()
let darkOnly = 0
const listeners = new Set<() => void>()

/**
 * Black only since 2026-10-19 (Huanrui: the top bar's sun-and-moon button
 * taken out, one colour scheme). A light choice kept from before is ignored.
 */
function read(): Theme {
  return 'dark'
}

function apply() {
  document.documentElement.dataset.theme = chosen === 'light' && darkOnly === 0 ? 'light' : 'dark'
}
apply()

function emit() {
  apply()
  for (const listener of listeners) listener()
}

function remember(next: Theme) {
  chosen = next
  try {
    window.localStorage.setItem(KEY, next)
  } catch {
    // Not remembered here; the account's copy still has it.
  }
  emit()
}

/** Chosen by the person: drawn, and kept with their account. */
export function setTheme(next: Theme) {
  remember(next)
  savePref('theme', next, 0)
}

let adopted = false
/** Once a visit, once signed in: take the skin the account remembers. */
export function adoptAccountTheme() {
  if (adopted) return
  adopted = true
  // Black only (2026-10-19): the account's old choice is no longer applied.
  readPref('theme')
    .then(() => undefined)
    .catch(() => {
      adopted = false
    })
}

/** The skin this person has chosen (not what an exempt page is drawn in). */
export function useTheme(): [Theme, (next: Theme) => void] {
  const theme = useSyncExternalStore(
    (listener) => {
      listeners.add(listener)
      return () => listeners.delete(listener)
    },
    () => chosen,
  )
  return [theme, setTheme]
}

/** This page is drawn dark whatever the skin, while `active`. */
export function useDarkOnly(active = true) {
  useEffect(() => {
    if (!active) return
    darkOnly += 1
    emit()
    return () => {
      darkOnly -= 1
      emit()
    }
  }, [active])
}
